import { CanvasTexture, PlaneGeometry, MeshBasicMaterial, Mesh, Vector3, ClampToEdgeWrapping, LinearFilter } from "three";

const RAY_BASE_ROTATION_SPEED = 0.5; // Base rotation speed in radians per second
const RAY_COUNT = 7; // Number of rays
const RAY_BASE_LENGTH_RATIO = 0.1; // Base length of each ray as % of canvas size
const RAY_MAX_ANGLE = (120 * Math.PI) / 180; // Maximum angle at tip of ray in radians (120 degrees)
const RAY_WIDTH_MIN_SCALE = 0.3; // Minimum width scale for sine animation
const RAY_WIDTH_MAX_SCALE = 1.0; // Maximum width scale for sine animation
const RAY_WIDTH_FREQUENCY = 2.0; // Frequency of width pulsing (cycles per second)

class LightRaysOverlay {
  rayPlane = null;
  rayCanvas = null;
  rayTexture = null;
  rayAnimationTime = 0;
  rayConfig = [];
  camera = null;
  canvasRect = null;
  winningCoin = null; // Reference to winning coin to track its position
  canvasWidth = 0; // Canvas texture width (with DPR)
  canvasHeight = 0; // Canvas texture height (with DPR)

  constructor(camera, canvasRect) {
    this.camera = camera;
    this.canvasRect = canvasRect;
    // Calculate canvas size with DPR (matching render targets)
    // Use the same calculation as render targets to ensure proper aspect ratio
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvasWidth = Math.floor(canvasRect.width * dpr);
    this.canvasHeight = Math.floor(canvasRect.height * dpr);
    // Store aspect ratio for calculations
    this.aspectRatio = canvasRect.width / canvasRect.height;
  }

  /**
   * Set the winning coin to track its position
   */
  setWinningCoin(coin) {
    this.winningCoin = coin;
  }

  /**
   * Convert 3D world position to 2D canvas texture coordinates
   */
  worldToCanvas(worldPosition) {
    const vector = worldPosition.clone();
    vector.project(this.camera);

    // Convert from normalized device coordinates (-1 to 1) to canvas texture coordinates
    // Use actual canvas texture size (with DPR)
    // In Three.js NDC: x=-1 is left, x=+1 is right; y=-1 is bottom, y=+1 is top
    // Canvas: x=0 is left, x=width is right; y=0 is top, y=height is bottom
    // Flip Y axis: NDC y=+1 (top) → canvas y=height (bottom), NDC y=-1 (bottom) → canvas y=0 (top)
    const x = ((vector.x + 1) / 2) * this.canvasWidth;
    const y = ((vector.y + 1) / 2) * this.canvasHeight;

    return { x, y };
  }

  /**
   * Create canvas for ray texture
   */
  createRayCanvas(width, height) {
    const canvas = document.createElement("canvas");
    // Set internal resolution (with DPR)
    canvas.width = width;
    canvas.height = height;
    // Set CSS size to logical size (without DPR) for proper texture mapping
    // The texture will use the internal resolution but map correctly
    return canvas;
  }

  /**
   * Draw all rays on the canvas as triangles extending from center
   * @param {HTMLCanvasElement} canvas - Canvas to draw on
   * @param {number} time - Animation time in seconds
   * @param {number} sizeScale - Size scale for rays (already calculated from distance)
   */
  drawRaysOnCanvas(canvas, time, sizeScale) {
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (sizeScale <= 0) {
      return;
    }

    // Get center point - either from winning coin position or canvas center
    let centerX = canvas.width / 2;
    let centerY = canvas.height / 2;

    if (this.winningCoin) {
      // Get winning coin's mesh world position (center of the coin)
      // Use mesh instead of container to get the actual coin center
      const worldPosition = new Vector3();
      this.winningCoin.mesh.getWorldPosition(worldPosition);

      // Convert to canvas coordinates
      const canvasPos = this.worldToCanvas(worldPosition);
      centerX = canvasPos.x;
      centerY = canvasPos.y;
    }

    // sizeScale is already calculated from distance, use it directly

    // Draw each ray
    this.rayConfig.forEach((config) => {
      // Calculate current rotation angle
      const currentAngle =
        config.initialAngle +
        RAY_BASE_ROTATION_SPEED * time * config.rotationSpeedMultiplier;

      // Calculate current width scale using sine curve
      const sineValue = Math.sin(time * RAY_WIDTH_FREQUENCY + config.phase);
      const widthScale =
        RAY_WIDTH_MIN_SCALE +
        (RAY_WIDTH_MAX_SCALE - RAY_WIDTH_MIN_SCALE) * ((sineValue + 1) / 2);

      // Calculate ray dimensions based on canvas diagonal to ensure rays reach corners
      // Use the diagonal to ensure rays extend to all edges regardless of aspect ratio
      const canvasDiagonal = Math.sqrt(canvas.width * canvas.width + canvas.height * canvas.height);
      const rayLength = canvasDiagonal * RAY_BASE_LENGTH_RATIO * sizeScale;

      // Calculate end width based on angle (120 degrees max)
      // Using trigonometry: width = 2 * length * tan(angle/2)
      const angle = RAY_MAX_ANGLE * widthScale;
      const endWidth = 2 * rayLength * Math.tan(angle / 2);

      // Skip if too small to be visible
      if (rayLength < 1 || endWidth < 0.5) {
        return;
      }

      // Save context
      ctx.save();

      // Translate to center and rotate
      ctx.translate(centerX, centerY);
      ctx.rotate(currentAngle);

      // Draw triangle extending from exact center outward
      // Triangle points: start point (at exact center), end point left, end point right
      const startDistance = 0; // Start at exact center for all rays
      const endDistance = startDistance + rayLength;

      ctx.beginPath();
      // Start point (near center, slightly offset)
      ctx.moveTo(startDistance, 0);
      // Left point at the end
      ctx.lineTo(endDistance, -endWidth / 2);
      // Right point at the end
      ctx.lineTo(endDistance, endWidth / 2);
      // Close triangle
      ctx.closePath();

      // Create linear gradient along the length of the ray
      // Gradient fades from opaque at center to transparent at edges
      const gradient = ctx.createLinearGradient(
        startDistance,
        0,
        endDistance,
        0
      );
      // Start with full opacity at center
      gradient.addColorStop(
        0,
        `rgba(${config.color.r}, ${config.color.g}, ${config.color.b}, 1.0)`
      );
      // Fully transparent at end
      gradient.addColorStop(
        1,
        `rgba(${config.color.r}, ${config.color.g}, ${config.color.b}, 0)`
      );

      // Fill triangle with gradient
      ctx.fillStyle = gradient;
      ctx.fill();

      // Restore context
      ctx.restore();
    });
  }

  /**
   * Setup ray system with canvas texture
   */
  setup(overlayPlaneGroup, foregroundLayer) {
    // Initialize ray configuration
    this.rayConfig = [];
    const baseAngleStep = (Math.PI * 2) / RAY_COUNT;

    // Color distribution: mix white and blue
    const colors = [
      { r: 255, g: 255, b: 255 }, // white
      { r: 121, g: 148, b: 255 }, // blue
      { r: 255, g: 255, b: 255 }, // white
      { r: 121, g: 148, b: 255 }, // blue
      { r: 255, g: 255, b: 255 }, // white
      { r: 121, g: 148, b: 255 }, // blue
      { r: 255, g: 255, b: 255 }, // white
    ];

    for (let i = 0; i < RAY_COUNT; i++) {
      // Base angle with random offset (±10° to ±15°)
      const randomOffset = Math.random() * 0.0873 - 0.04365; // ±2.5° in radians (approximately ±10-15°)
      const initialAngle = i * baseAngleStep + randomOffset;

      // Random rotation speed multiplier (0.7x to 1.3x)
      const rotationSpeedMultiplier = 0.7 + Math.random() * 0.6;

      // Random phase offset for sine wave (0 to 2π)
      const phase = Math.random() * Math.PI * 2;

      this.rayConfig.push({
        color: colors[i],
        initialAngle,
        rotationSpeedMultiplier,
        phase,
      });
    }

    // Create canvas with DPR-aware size
    this.rayCanvas = this.createRayCanvas(this.canvasWidth, this.canvasHeight);

    // Create texture from canvas
    this.rayTexture = new CanvasTexture(this.rayCanvas);
    // Set texture properties for proper display
    this.rayTexture.wrapS = ClampToEdgeWrapping;
    this.rayTexture.wrapT = ClampToEdgeWrapping;
    this.rayTexture.minFilter = LinearFilter;
    this.rayTexture.magFilter = LinearFilter;
    this.rayTexture.flipY = false; // Don't flip Y axis for canvas textures
    this.rayTexture.needsUpdate = true;

    // Calculate plane size to fill camera view
    // Distance from camera to overlay plane group position
    const OVERLAY_PLANE_GROUP_Z = -12;
    const distance = this.camera.position.z - OVERLAY_PLANE_GROUP_Z;
    const fov = this.camera.fov * (Math.PI / 180);
    const planeHeight = 2 * Math.tan(fov / 2) * distance;
    const aspect = this.canvasRect.width / this.canvasRect.height;
    const planeWidth = planeHeight * aspect;

    // Create plane geometry for rays
    const rayPlaneGeometry = new PlaneGeometry(planeWidth, planeHeight);
    const rayPlaneMaterial = new MeshBasicMaterial({
      map: this.rayTexture,
      transparent: true,
      opacity: 0, // Start invisible, will be controlled by blur amount
    });
    this.rayPlane = new Mesh(rayPlaneGeometry, rayPlaneMaterial);

    // Position at same Z as overlay plane group (relative to group, so 0)
    this.rayPlane.position.set(0, 0, 0);
    this.rayPlane.lookAt(this.camera.position);

    // Set to foreground layer (ensure all descendants are also set)
    this.rayPlane.layers.set(foregroundLayer);
    this.rayPlane.traverse((child) => {
      if (child.layers) {
        child.layers.set(foregroundLayer);
      }
    });

    // Add to overlay plane group
    overlayPlaneGroup.add(this.rayPlane);

    // Initial draw with farthest scale (0.5) - coin starts far from camera
    this.drawRaysOnCanvas(this.rayCanvas, 0, 0.5);
    this.rayTexture.needsUpdate = true;
  }

  /**
   * Update rays animation
   * @param {number} deltaTime - Time delta in milliseconds
   * @param {number} blurAmount - Blur amount (0-1), used for opacity
   */
  update(deltaTime, blurAmount) {
    if (!this.rayCanvas || !this.rayTexture || !this.rayConfig.length) {
      return;
    }

    // Convert deltaTime from milliseconds to seconds
    const deltaSeconds = deltaTime / 1000;
    this.rayAnimationTime += deltaSeconds;

    // Calculate size scale based on winning coin's distance from camera
    // Closer to camera = larger rays
    // Scale: 2.0 at closest position, 0.5 at farthest position
    let distanceScale = 0.5; // Default to farthest scale if no coin
    if (this.winningCoin) {
      const coinPosition = new Vector3();
      this.winningCoin.mesh.getWorldPosition(coinPosition);

      // Calculate distance from camera to coin
      const distance = this.camera.position.distanceTo(coinPosition);

      // Based on RevealAnimationManager:
      // Closest: TARGET_Z_OFFSET_FROM_CAMERA = -3.5 (distance from camera = 3.5)
      // Farthest: END_Z_POSITION = -10 (distance from camera = camera.position.z + 10)
      const closestDistance = 3.5; // Distance when coin is at target position
      const farthestDistance = this.camera.position.z + 10; // Distance when coin is at END_Z_POSITION

      // Normalize distance to 0-1 range (0 = closest, 1 = farthest)
      const normalizedDistance = Math.max(0, Math.min(1, (distance - closestDistance) / (farthestDistance - closestDistance)));

      // Map to scale range: 2.0 (closest) to 0.5 (farthest)
      // Invert so closer = larger scale
      distanceScale = 2.0 - (2.0 - 0.5) * normalizedDistance;
    }

    // Redraw rays on canvas (center will follow winning coin if set)
    // Use distance scale for size, not blur
    this.drawRaysOnCanvas(this.rayCanvas, this.rayAnimationTime, distanceScale);
    this.rayTexture.needsUpdate = true;

    // Update ray plane opacity based on blur amount (more blur = more opaque)
    if (this.rayPlane && this.rayPlane.material) {
      this.rayPlane.material.opacity = blurAmount;
    }
  }

  /**
   * Update canvas size when viewport changes
   * @param {DOMRect} canvasRect - Updated canvas rect
   */
  updateCanvasSize(canvasRect = null) {
    if (canvasRect) {
      this.canvasRect = canvasRect;
      // Recalculate canvas size with DPR
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvasWidth = Math.floor(canvasRect.width * dpr);
      this.canvasHeight = Math.floor(canvasRect.height * dpr);
      this.aspectRatio = canvasRect.width / canvasRect.height;

      // Resize canvas if it exists
      if (this.rayCanvas) {
        this.rayCanvas.width = this.canvasWidth;
        this.rayCanvas.height = this.canvasHeight;
        // Force texture update after resize
        if (this.rayTexture) {
          this.rayTexture.needsUpdate = true;
        }
      }
    }

    if (!this.rayPlane || !this.rayPlane.geometry) {
      return;
    }

    // Calculate plane size to fill camera view
    // Distance from camera to overlay plane group position
    const OVERLAY_PLANE_GROUP_Z = -12;
    const distance = this.camera.position.z - OVERLAY_PLANE_GROUP_Z;
    const fov = this.camera.fov * (Math.PI / 180);
    const planeHeight = 2 * Math.tan(fov / 2) * distance;
    const aspect = this.canvasRect.width / this.canvasRect.height;
    const planeWidth = planeHeight * aspect;

    // Update geometry
    this.rayPlane.geometry.dispose();
    this.rayPlane.geometry = new PlaneGeometry(planeWidth, planeHeight);
    this.rayPlane.lookAt(this.camera.position);
  }

  /**
   * Update ray plane size to match other overlay planes
   */
  updatePlaneSize(width, height) {
    if (!this.rayPlane || !this.rayPlane.geometry) {
      return;
    }
    this.rayPlane.geometry.dispose();
    this.rayPlane.geometry = new PlaneGeometry(width, height);
    this.rayPlane.lookAt(this.camera.position);
  }

  /**
   * Update ray plane orientation to face camera
   */
  updateOrientation() {
    if (this.rayPlane) {
      this.rayPlane.lookAt(this.camera.position);
    }
  }
}

export default LightRaysOverlay;

